/**
 * Mission Control - prioritized operational overview.
 */

import { useEffect, useRef, useState, type ElementRef } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  Archive,
  ArchiveRestore,
  ArrowRight,
  CheckCircle2,
  Clock,
  Inbox,
  MessageSquare,
  Send,
  Target,
  XCircle,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import { Layout } from '@/components/layout/Layout';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import {
  useArchiveCampaign,
  useIsArchiveCampaignPending,
  type ArchiveCampaignVariables,
} from '@/hooks/use-archive-campaign';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import {
  isArchivedCampaign,
  useActivityStream,
  useCampaignPulse,
  useNeedsAttention,
  type ActivityItem,
  type CampaignPulse,
  type NeedsAttentionItem,
} from '@/hooks/useMissionControl';
import { useGhostRisk } from '@/hooks/useRevenueIntelligence';
import { useLinkedInDraftCounts } from '@/hooks/useLinkedInDrafts';
import { formatRelativeTime, getRecencyColor } from '@/lib/intelligence-helpers';
import { cn } from '@/lib/utils';

const GOAL_LABELS: Record<string, string> = {
  book_meeting: 'Book Meeting',
  qualify: 'Qualify',
  nurture: 'Nurture',
};

const EVENT_ICONS: Record<string, LucideIcon> = {
  reply_received: MessageSquare,
  draft_approved: CheckCircle2,
  message_sent: Send,
  draft_rejected: XCircle,
  new_lead: Zap,
};

const EVENT_TEXT: Record<string, string> = {
  reply_received: 'replied',
  draft_approved: 'Draft approved for',
  message_sent: 'Message sent to',
  draft_rejected: 'Draft rejected for',
  new_lead: 'New lead:',
};

const ATTENTION_ICONS: Record<string, LucideIcon> = {
  ghost_risk: AlertTriangle,
  stale_draft: Clock,
  failed_draft: XCircle,
};

function CampaignPulseCard({
  campaign,
  isAdmin,
  canArchive,
  isUpdating,
  disableArchiveAction,
  archiveButtonRef,
  onArchiveChange,
}: {
  campaign: CampaignPulse;
  isAdmin: boolean;
  canArchive: boolean;
  isUpdating: boolean;
  disableArchiveAction: boolean;
  archiveButtonRef: (element: HTMLButtonElement | null) => void;
  onArchiveChange: (variables: ArchiveCampaignVariables) => void;
}): JSX.Element {
  const isArchived = isArchivedCampaign(campaign);
  const recencyClass = campaign.last_activity
    ? getRecencyColor(campaign.last_activity)
    : 'text-muted-foreground';
  const ArchiveIcon = isArchived ? ArchiveRestore : Archive;

  const updateArchiveState = (): void => {
    onArchiveChange({
      campaignId: campaign.heyreach_campaign_id,
      archived: !isArchived,
      campaignOrganizationId: campaign.organization_id,
    });
  };

  return (
    <Card className="h-full overflow-hidden transition-colors hover:border-border-strong hover:bg-surface-hover">
      <Link
        to={`/campaigns/${campaign.heyreach_campaign_id}`}
        className="block rounded-t-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
        aria-label={`Open campaign ${campaign.campaign_name}`}
      >
        <CardContent className="space-y-4 p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <h3 className="truncate font-semibold text-foreground">
                {campaign.campaign_name}
              </h3>
              {campaign.product_line && (
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {campaign.product_line}
                </p>
              )}
            </div>
            {campaign.goal && (
              <Badge variant="outline" className="shrink-0 text-xs">
                {GOAL_LABELS[campaign.goal] || campaign.goal}
              </Badge>
            )}
          </div>

          <div className="space-y-1.5">
            <div
              className="flex h-2 w-full overflow-hidden rounded-full bg-muted"
              aria-label={`${campaign.sent_count} sent, ${campaign.approved_count} approved, ${campaign.pending_drafts} pending`}
            >
              {campaign.sent_count > 0 && (
                <span
                  className="bg-status-success"
                  style={{
                    width: `${(campaign.sent_count / Math.max(campaign.total_drafts, 1)) * 100}%`,
                  }}
                />
              )}
              {campaign.approved_count > 0 && (
                <span
                  className="bg-primary"
                  style={{
                    width: `${(campaign.approved_count / Math.max(campaign.total_drafts, 1)) * 100}%`,
                  }}
                />
              )}
              {campaign.pending_drafts > 0 && (
                <span
                  className="bg-status-warning"
                  style={{
                    width: `${(campaign.pending_drafts / Math.max(campaign.total_drafts, 1)) * 100}%`,
                  }}
                />
              )}
            </div>
            <div className="flex justify-between gap-2 text-xs text-muted-foreground">
              <span>{campaign.total_leads} leads</span>
              <span>{campaign.sent_count} sent</span>
              {campaign.pending_drafts > 0 && (
                <span>{campaign.pending_drafts} pending</span>
              )}
            </div>
          </div>

          <dl className="grid grid-cols-3 gap-2 text-center">
            <div>
              <dd className="text-lg font-semibold text-foreground">
                {campaign.response_rate ?? 0}%
              </dd>
              <dt className="text-xs text-muted-foreground">Response</dt>
            </div>
            <div>
              <dd className="text-lg font-semibold text-foreground">
                {campaign.leads_contacted}
              </dd>
              <dt className="text-xs text-muted-foreground">Contacted</dt>
            </div>
            <div>
              <dd className="text-lg font-semibold text-foreground">
                {campaign.edit_rate !== null ? `${campaign.edit_rate}%` : '—'}
              </dd>
              <dt className="text-xs text-muted-foreground">Edit rate</dt>
            </div>
          </dl>

          <div className="flex items-center gap-1.5 text-xs">
            <Clock className="h-3 w-3 text-muted-foreground" aria-hidden="true" />
            <span className={recencyClass}>
              {campaign.last_activity
                ? formatRelativeTime(campaign.last_activity)
                : 'No activity'}
            </span>
            {campaign.pending_drafts > 0 && (
              <span className="ml-auto text-status-warning">
                {campaign.pending_drafts} awaiting review
              </span>
            )}
          </div>
        </CardContent>
      </Link>
      <CardFooter className="justify-between gap-3 border-t px-5 py-3">
        {isArchived ? (
          <Badge variant="secondary">Archived</Badge>
        ) : (
          <span aria-hidden="true" />
        )}
        {isAdmin && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="min-h-11"
            ref={archiveButtonRef}
            onClick={updateArchiveState}
            disabled={!canArchive || disableArchiveAction}
            aria-busy={isUpdating}
            aria-label={`${isArchived ? 'Unarchive' : 'Archive'} campaign ${campaign.campaign_name}`}
          >
            <ArchiveIcon aria-hidden="true" />
            {isArchived ? 'Unarchive' : 'Archive'}
          </Button>
        )}
      </CardFooter>
    </Card>
  );
}

interface CampaignPulseSectionProps {
  campaigns: CampaignPulse[] | undefined;
  isError: boolean;
  isLoading: boolean;
}

export function CampaignPulseSection({
  campaigns,
  isError,
  isLoading,
}: CampaignPulseSectionProps): JSX.Element {
  const { isAdmin } = useAuth();
  const [showArchived, setShowArchived] = useState(false);
  const archiveCampaign = useArchiveCampaign();
  const isArchiveMutationPending = useIsArchiveCampaignPending();
  const showArchivedToggleRef = useRef<ElementRef<typeof Switch>>(null);
  const archiveButtonRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const focusRestoreCampaignIdRef = useRef<string | null>(null);
  // F4: set in the archive handler, consumed by the effect below. On the
  // FIRST-EVER archive the "Show archived" switch is not in the DOM yet at
  // click time (it only mounts once archivedCount > 0), so focusing it
  // there is a no-op. This is STATE (not a plain ref) specifically so that
  // setting it schedules a re-render and re-runs the effect immediately —
  // needed for the case where the switch is already mounted, since
  // archivedCount alone would not change in that case. When the switch has
  // not mounted yet, the same effect re-runs again once the optimistic
  // update lands and archivedCount changes, by which point the switch ref
  // is populated.
  const [pendingShowArchivedFocus, setPendingShowArchivedFocus] = useState(false);
  const allCampaigns = campaigns ?? [];
  const activeCampaigns = allCampaigns.filter(
    (campaign) => !isArchivedCampaign(campaign),
  );
  const archivedCount = allCampaigns.length - activeCampaigns.length;
  const visibleCampaigns = showArchived ? allCampaigns : activeCampaigns;

  // F8/F4: once an optimistic archive removes the currently-focused card
  // from the default view, focus lands on the "Show archived" toggle
  // instead of being dropped to <body> — resolved here (after render) so it
  // works even when this archive is the first one and the switch has only
  // just mounted. If the write is rolled back, focus returns to that
  // campaign's own Archive button — but only once it is back in the DOM
  // (the effect below runs post-commit, after the rollback has re-rendered
  // it).
  useEffect(() => {
    if (pendingShowArchivedFocus && showArchivedToggleRef.current) {
      showArchivedToggleRef.current.focus();
      setPendingShowArchivedFocus(false);
    }
  }, [pendingShowArchivedFocus, archivedCount]);

  // Rollback focus-restore: onError's mutation-state flip (isError) and its
  // queryClient.setQueryData rollback (which puts this campaign's card back
  // in `campaigns`) are two SEPARATE subscriptions (useMutation vs.
  // useQuery/props) — they usually land in the same commit, but nothing
  // guarantees it. If isError becomes observable here in a render BEFORE
  // the rolled-back row has propagated back into `campaigns`, the button
  // for campaignId is not in the DOM yet, so archiveButtonRefs has no entry
  // for it — and a dependency array of only [isError, variables] would
  // never re-run this effect again once those two values stop changing,
  // permanently losing the focus restore. Depending on `campaigns` as well
  // makes the effect retry on every subsequent data change too, and only
  // consuming (clearing) focusRestoreCampaignIdRef once the button is
  // actually found keeps a still-pending restore alive across renders
  // instead of discarding it on the first miss.
  useEffect(() => {
    const campaignId = focusRestoreCampaignIdRef.current;
    if (
      campaignId &&
      archiveCampaign.isError &&
      archiveCampaign.variables?.campaignId === campaignId
    ) {
      const button = archiveButtonRefs.current.get(campaignId);
      if (button) {
        button.focus();
        focusRestoreCampaignIdRef.current = null;
      }
    }
  }, [archiveCampaign.isError, archiveCampaign.variables, campaigns]);

  const registerArchiveButtonRef =
    (campaignId: string) =>
    (element: HTMLButtonElement | null): void => {
      if (element) {
        archiveButtonRefs.current.set(campaignId, element);
      } else {
        archiveButtonRefs.current.delete(campaignId);
      }
    };

  const handleArchiveChange = (variables: ArchiveCampaignVariables): void => {
    const removesFocusedCardFromDefaultView =
      variables.archived && !showArchived;
    if (removesFocusedCardFromDefaultView) {
      focusRestoreCampaignIdRef.current = variables.campaignId;
      setPendingShowArchivedFocus(true);
    }
    archiveCampaign.mutate(variables);
  };

  return (
    <section aria-labelledby="campaigns-heading">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
        <h2 id="campaigns-heading" className="text-lg font-semibold">
          Campaigns
        </h2>
        <div className="flex flex-wrap items-center justify-end gap-4">
          {campaigns && (
            <span className="text-sm text-muted-foreground">
              {activeCampaigns.length} active
            </span>
          )}
          {archivedCount > 0 && (
            <div className="flex items-center gap-2">
              <Switch
                id="show-archived-campaigns"
                ref={showArchivedToggleRef}
                checked={showArchived}
                onCheckedChange={setShowArchived}
              />
              <Label
                htmlFor="show-archived-campaigns"
                className="text-sm text-muted-foreground"
              >
                Show archived ({archivedCount})
              </Label>
            </div>
          )}
        </div>
      </div>

      {isLoading && (
        <div aria-label="Loading campaigns" className="grid gap-4 sm:grid-cols-2">
          {[1, 2, 3, 4].map((item) => (
            <Skeleton key={item} className="h-64 rounded-xl" />
          ))}
        </div>
      )}
      {!isLoading && isError && (
        <Card>
          <CardContent role="alert" className="p-8 text-center">
            <Target className="mx-auto h-8 w-8 text-destructive" aria-hidden="true" />
            <p className="mt-3 font-medium">Campaigns could not be loaded.</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Refresh the page to try again.
            </p>
          </CardContent>
        </Card>
      )}
      {!isLoading && !isError && campaigns?.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <Target className="mx-auto h-8 w-8 text-muted-foreground" aria-hidden="true" />
            <p className="mt-3 font-medium">No campaigns yet</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Campaigns appear when campaign activity is available.
            </p>
          </CardContent>
        </Card>
      )}
      {!isLoading &&
        !isError &&
        allCampaigns.length > 0 &&
        visibleCampaigns.length === 0 && (
          <Card>
            <CardContent role="status" className="p-8 text-center">
              <Archive className="mx-auto h-8 w-8 text-muted-foreground" aria-hidden="true" />
              <p className="mt-3 font-medium">All campaigns are archived</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Use “Show archived” to view or return a campaign to the default list.
              </p>
            </CardContent>
          </Card>
        )}
      {!isLoading && !isError && visibleCampaigns.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2">
          {visibleCampaigns.map((campaign) => (
            <CampaignPulseCard
              key={campaign.heyreach_campaign_id}
              campaign={campaign}
              isAdmin={isAdmin}
              canArchive={archiveCampaign.canArchive}
              isUpdating={
                archiveCampaign.isPending &&
                archiveCampaign.variables?.campaignId ===
                  campaign.heyreach_campaign_id
              }
              disableArchiveAction={isArchiveMutationPending}
              archiveButtonRef={registerArchiveButtonRef(
                campaign.heyreach_campaign_id,
              )}
              onArchiveChange={handleArchiveChange}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function ActivityStreamItem({ item }: { item: ActivityItem }): JSX.Element {
  const Icon = EVENT_ICONS[item.eventType] || MessageSquare;

  return (
    <Link
      to="/inbox"
      className="flex min-h-11 items-center gap-3 rounded-lg px-3 py-2.5 transition-colors hover:bg-surface-hover"
    >
      <Icon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      <span className="min-w-0 flex-1 truncate text-sm text-foreground">
        <span className="text-muted-foreground">{EVENT_TEXT[item.eventType]}</span>{' '}
        <span className="font-medium">{item.leadName}</span>
        {item.leadCompany && (
          <span className="text-muted-foreground"> · {item.leadCompany}</span>
        )}
      </span>
      <span className="shrink-0 text-xs text-muted-foreground">
        {formatRelativeTime(item.timestamp)}
      </span>
    </Link>
  );
}

function NeedsAttentionItemLink({ item }: { item: NeedsAttentionItem }): JSX.Element {
  const Icon = ATTENTION_ICONS[item.type] || AlertTriangle;

  return (
    <Link
      to={item.deepLink}
      className={cn(
        'flex min-h-14 items-center gap-3 rounded-lg border p-3 transition-colors hover:bg-surface-hover',
        item.urgency === 'high'
          ? 'border-destructive/40 bg-destructive/5'
          : 'border-status-warning/40 bg-status-warning/5'
      )}
      aria-label={`${item.leadName}: ${item.reason}`}
    >
      <Icon
        className={cn(
          'h-4 w-4 shrink-0',
          item.urgency === 'high' ? 'text-destructive' : 'text-status-warning'
        )}
        aria-hidden="true"
      />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-foreground">
          {item.leadName}
          {item.leadCompany && (
            <span className="text-muted-foreground"> · {item.leadCompany}</span>
          )}
        </span>
        <span className="block text-xs text-muted-foreground">{item.reason}</span>
      </span>
      <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
    </Link>
  );
}

function MetricCard({
  icon: Icon,
  label,
  value,
}: {
  icon: LucideIcon;
  label: string;
  value: number;
}): JSX.Element {
  return (
    <Card>
      <CardContent className="p-4 text-center">
        <Icon className="mx-auto h-5 w-5 text-primary" aria-hidden="true" />
        <p className="mt-1 text-2xl font-bold">{value}</p>
        <p className="text-xs text-muted-foreground">{label}</p>
      </CardContent>
    </Card>
  );
}

export default function MissionControl(): JSX.Element {
  useDocumentTitle('Mission Control - Tacit');

  const campaignPulse = useCampaignPulse();
  const activityStream = useActivityStream(15);
  const ghostRisk = useGhostRisk(10);
  const draftCounts = useLinkedInDraftCounts();
  const attention = useNeedsAttention(ghostRisk.data?.items || []);

  const pendingCount = draftCounts.data?.pending_approval ?? 0;
  const attentionLoading = attention.isLoading || ghostRisk.isLoading;
  const attentionError = attention.isError || ghostRisk.isError;
  const visibleAttentionItems = attention.items.slice(0, 5);
  // r5-F5: archiving a campaign is a DISPLAY decision, not an operational one
  // (see supabase/migrations/20260806140000_campaign_archiving.sql) — no
  // lead or message is deleted, and webhook ingest for an archived campaign
  // continues. "Total leads"/"Messages sent" below are labeled as historical
  // TOTALS, so they must be computed from the FULL pulse dataset, not just
  // non-archived rows — otherwise archiving a campaign would silently
  // subtract its leads/messages from a card that claims to be a total.
  // `activeCampaigns` stays reserved for the "Active campaigns" count and
  // the default campaign list, where its name is accurate.
  const allCampaigns = campaignPulse.data ?? [];
  const activeCampaigns = allCampaigns.filter(
    (campaign) => !isArchivedCampaign(campaign),
  );

  return (
    <Layout>
      <div className="mx-auto w-full max-w-7xl space-y-8 p-4 sm:p-6 lg:p-8">
        <header>
          <h1 className="text-3xl font-bold tracking-tight">Mission Control</h1>
          <p className="mt-1 text-muted-foreground">
            Review what needs action, then track campaign progress.
          </p>
        </header>

        <section aria-labelledby="needs-attention-heading">
          <Card>
            <CardHeader className="flex-row items-center justify-between gap-4 pb-3">
              <div>
                <h2
                  id="needs-attention-heading"
                  className="flex items-center gap-2 text-lg font-semibold"
                >
                  <AlertTriangle
                    className="h-5 w-5 text-status-warning"
                    aria-hidden="true"
                  />
                  Needs attention
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Highest-priority work across your sales queue.
                </p>
              </div>
              {attention.items.length > 5 && (
                <Button
                  asChild
                  variant="outline"
                  size="sm"
                  className="min-h-11 md:min-h-11"
                >
                  <Link to="/inbox?status=pending_approval">
                    View all
                    <span className="sr-only">
                      {' '}
                      {attention.items.length} items needing attention
                    </span>
                  </Link>
                </Button>
              )}
            </CardHeader>
            <CardContent className="space-y-2">
              {attentionLoading && (
                <div aria-label="Loading needs attention" className="space-y-2">
                  {[1, 2, 3].map((item) => (
                    <Skeleton key={item} className="h-14 w-full" />
                  ))}
                </div>
              )}
              {!attentionLoading && attentionError && (
                <div
                  role="alert"
                  className="rounded-lg border border-destructive/40 bg-destructive/5 p-4"
                >
                  <p className="font-medium">Needs attention could not be loaded.</p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Refresh the page to try again. No queue items were changed.
                  </p>
                </div>
              )}
              {!attentionLoading &&
                !attentionError &&
                visibleAttentionItems.map((item) => (
                  <NeedsAttentionItemLink key={`${item.type}-${item.id}`} item={item} />
                ))}
              {!attentionLoading && !attentionError && attention.items.length === 0 && (
                <div
                  role="status"
                  className="flex min-h-20 items-center gap-3 rounded-lg border border-status-success/40 bg-status-success/5 p-4"
                >
                  <CheckCircle2
                    className="h-5 w-5 shrink-0 text-status-success"
                    aria-hidden="true"
                  />
                  <span>
                    <span className="block font-medium">All clear</span>
                    <span className="block text-sm text-muted-foreground">
                      There are no priority items waiting for action.
                    </span>
                  </span>
                </div>
              )}
            </CardContent>
          </Card>
        </section>

        <section aria-labelledby="overview-heading">
          <h2 id="overview-heading" className="mb-4 text-lg font-semibold">
            Overview
          </h2>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Link
              to="/inbox?status=pending_approval"
              className="rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              aria-label={`${pendingCount} pending drafts, open Inbox`}
            >
              <Card className="h-full transition-colors hover:border-border-strong hover:bg-surface-hover">
                <CardContent className="p-4 text-center">
                  <Inbox className="mx-auto h-5 w-5 text-status-warning" aria-hidden="true" />
                  <p className="mt-1 text-2xl font-bold">{pendingCount}</p>
                  <p className="text-xs text-muted-foreground">Pending drafts</p>
                </CardContent>
              </Card>
            </Link>
            <MetricCard
              icon={Target}
              label="Active campaigns"
              value={activeCampaigns.length}
            />
            <MetricCard
              icon={MessageSquare}
              label="Total leads"
              value={
                allCampaigns.reduce(
                  (sum, campaign) => sum + campaign.total_leads,
                  0
                )
              }
            />
            <MetricCard
              icon={Send}
              label="Messages sent"
              value={
                allCampaigns.reduce(
                  (sum, campaign) => sum + campaign.sent_count,
                  0
                )
              }
            />
          </div>
        </section>

        <CampaignPulseSection
          campaigns={campaignPulse.data}
          isError={campaignPulse.isError}
          isLoading={campaignPulse.isLoading}
        />

        <section aria-labelledby="recent-activity-heading">
          <Card>
            <CardHeader className="pb-3">
              <h2 id="recent-activity-heading" className="text-lg font-semibold">
                Recent activity
              </h2>
            </CardHeader>
            <CardContent>
              {activityStream.isLoading && (
                <div aria-label="Loading recent activity" className="space-y-2">
                  {[1, 2, 3, 4, 5].map((item) => (
                    <Skeleton key={item} className="h-11 w-full" />
                  ))}
                </div>
              )}
              {!activityStream.isLoading && activityStream.isError && (
                <p role="alert" className="py-4 text-sm text-destructive">
                  Recent activity could not be loaded.
                </p>
              )}
              {!activityStream.isLoading &&
                !activityStream.isError &&
                activityStream.data?.length === 0 && (
                  <p className="py-4 text-center text-sm text-muted-foreground">
                    No activity yet.
                  </p>
                )}
              {!activityStream.isLoading &&
                !activityStream.isError &&
                activityStream.data &&
                activityStream.data.length > 0 && (
                  <div className="space-y-0.5">
                    {activityStream.data.map((item) => (
                      <ActivityStreamItem key={item.id} item={item} />
                    ))}
                  </div>
                )}
            </CardContent>
          </Card>
        </section>
      </div>
    </Layout>
  );
}
